import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import api, { walletAPI } from '../services/api';
import { getPaymentQrImageUrl } from '../config';
import { Wallet, Banknote, ArrowLeftRight, Gift, FileText, Smartphone, Building2, CreditCard } from 'lucide-react';
import { toast } from '../components/Toast';
import logger from '../utils/logger';

const PlatformWallet = () => {
  useContext(AuthContext);
  const [wallet, setWallet] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [depositRequests, setDepositRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Modal dépôt
  const [showDepositModal, setShowDepositModal] = useState(false);
  const [depositForm, setDepositForm] = useState({
    amount: '',
    payment_method: 'orange_money',
    payment_details: {
      phone: '',
      transaction_id: ''
    }
  });
  const [submitting, setSubmitting] = useState(false);
  const [paymentInstructions, setPaymentInstructions] = useState('');
  const [paymentQrImage, setPaymentQrImage] = useState('');

  // Charger les données
  useEffect(() => {
    loadWalletData();
  }, []);

  const loadWalletData = async () => {
    try {
      setLoading(true);
      const [walletRes, requestsRes, instructionsRes] = await Promise.all([
        walletAPI.get(),
        walletAPI.getDepositRequests(),
        api.get('/services/payment-instructions')
      ]);
      
      if (walletRes.data.success) {
        setWallet(walletRes.data.wallet);
        setTransactions(walletRes.data.recent_transactions || []);
      }
      
      if (requestsRes.data.success) {
        setDepositRequests(requestsRes.data.requests || []);
      }

      if (instructionsRes.data.payment_instructions) {
        setPaymentInstructions(instructionsRes.data.payment_instructions);
      }
      if (instructionsRes.data.payment_qr_image) {
        setPaymentQrImage(instructionsRes.data.payment_qr_image);
      }
    } catch (err) {
      setError('Erreur lors du chargement du portefeuille');
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadMoreTransactions = async () => {
    try {
      const res = await walletAPI.getTransactions({ limit: 50, offset: transactions.length });
      if (res.data.success) {
        setTransactions([...transactions, ...res.data.transactions]);
      }
    } catch (err) {
      logger.error(err);
    }
  };

  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    
    if (!depositForm.amount || parseFloat(depositForm.amount) < 1000) {
      toast.warning('Montant minimum: 1 000 XOF');
      return;
    }
    
    try {
      setSubmitting(true);
      const res = await walletAPI.createDepositRequest({
        amount: parseFloat(depositForm.amount),
        payment_method: depositForm.payment_method,
        payment_details: depositForm.payment_details
      });
      
      if (res.data.success) {
        toast.success('Demande de dépôt créée ! En attente de validation par l\'équipe.');
        setShowDepositModal(false);
        setDepositForm({
          amount: '',
          payment_method: 'orange_money',
          payment_details: { phone: '', transaction_id: '' }
        });
        loadWalletData();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la demande');
    } finally {
      setSubmitting(false);
    }
  };

  const formatAmount = (amount) => {
    const num = parseFloat(amount);
    const formatted = new Intl.NumberFormat('fr-FR').format(Math.abs(num));
    return num < 0 ? `-${formatted}` : formatted;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTransactionIcon = (type) => {
    switch (type) {
      case 'deposit': return <Wallet className="w-5 h-5 text-green-600" />;
      case 'payment': return <Banknote className="w-5 h-5 text-red-600" />;
      case 'refund': return <ArrowLeftRight className="w-5 h-5 text-blue-600" />;
      case 'bonus': return <Gift className="w-5 h-5 text-purple-600" />;
      default: return <FileText className="w-5 h-5 text-gray-600" />;
    }
  };

  const getTransactionLabel = (type) => {
    switch (type) {
      case 'deposit': return 'Dépôt';
      case 'payment': return 'Paiement';
      case 'refund': return 'Remboursement';
      case 'bonus': return 'Bonus';
      default: return 'Ajustement';
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full">En attente</span>;
      case 'approved':
        return <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-800 rounded-full">Approuvé</span>;
      case 'rejected':
        return <span className="px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded-full">Rejeté</span>;
      default:
        return null;
    }
  };

  const paymentMethods = [
    { value: 'orange_money', label: 'Orange Money', icon: <span className="w-4 h-4 bg-orange-500 rounded-full inline-block" /> },
    { value: 'mtn_money', label: 'MTN Mobile Money', icon: <span className="w-4 h-4 bg-yellow-500 rounded-full inline-block" /> },
    { value: 'mobile_money', label: 'Autre Mobile Money', icon: <Smartphone className="w-4 h-4" /> },
    { value: 'bank_transfer', label: 'Virement Bancaire', icon: <Building2 className="w-4 h-4" /> },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement du portefeuille...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-3">
            <CreditCard className="w-8 h-8 text-indigo-600" />
            <span>Portefeuille Plateforme</span>
          </h1>
          <p className="text-gray-600 mt-2">
            Gérez vos fonds pour payer les commissions plateforme (3%) sur vos projets
          </p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        {/* Carte solde */}
        <div className="bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-xl p-6 mb-8 text-white">
          <div className="flex justify-between items-start mb-6">
            <div>
              <p className="text-indigo-100 text-sm font-medium">Solde disponible</p>
              <p className="text-4xl font-bold mt-1">
                {formatAmount(wallet?.balance || 0)} <span className="text-xl font-normal">XOF</span>
              </p>
            </div>
            <button
              onClick={() => setShowDepositModal(true)}
              className="px-4 py-2 bg-white text-indigo-600 font-semibold rounded-xl hover:bg-indigo-50 transition flex items-center gap-2"
            >
              <span>➕</span>
              <span>Ajouter des fonds</span>
            </button>
          </div>
          
          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-indigo-400/30">
            <div>
              <p className="text-indigo-100 text-xs">Total déposé</p>
              <p className="text-xl font-semibold">{formatAmount(wallet?.total_deposited || 0)} XOF</p>
            </div>
            <div>
              <p className="text-indigo-100 text-xs">Total dépensé</p>
              <p className="text-xl font-semibold">{formatAmount(wallet?.total_spent || 0)} XOF</p>
            </div>
          </div>
        </div>

        {/* Informations sur le portefeuille */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-8">
          <h3 className="font-semibold text-blue-800 flex items-center gap-2 mb-2">
            <span>ℹ️</span>
            <span>Comment ça fonctionne ?</span>
          </h3>
          <ul className="text-blue-700 text-sm space-y-1">
            <li>• Déposez des fonds via Mobile Money ou virement bancaire</li>
            <li>• Vos dépôts sont validés manuellement par notre équipe (sous 24h)</li>
            <li>• Les commissions plateforme (3%) sont déduites automatiquement de votre solde</li>
            <li>• Si votre solde est insuffisant, le projet sera marqué "en attente de paiement"</li>
          </ul>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Demandes de dépôt en attente */}
          {depositRequests.filter(r => r.status === 'pending').length > 0 && (
            <div className="md:col-span-2 bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-2">
              <h3 className="font-semibold text-yellow-800 flex items-center gap-2 mb-3">
                <span>⏳</span>
                <span>Dépôts en attente de validation</span>
              </h3>
              <div className="space-y-2">
                {depositRequests.filter(r => r.status === 'pending').map(request => (
                  <div key={request.id} className="bg-white p-3 rounded-lg flex justify-between items-center">
                    <div>
                      <p className="font-medium text-gray-800">{formatAmount(request.amount)} XOF</p>
                      <p className="text-xs text-gray-500">{formatDate(request.created_at)}</p>
                    </div>
                    {getStatusBadge(request.status)}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Historique des transactions */}
          <div className="md:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-800">📜 Historique des transactions</h2>
            </div>
            
            {transactions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p className="text-4xl mb-2">📭</p>
                <p>Aucune transaction pour le moment</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {transactions.map(tx => (
                  <div key={tx.id} className="px-6 py-4 hover:bg-gray-50 transition">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{getTransactionIcon(tx.type)}</span>
                        <div>
                          <p className="font-medium text-gray-800">{tx.description || getTransactionLabel(tx.type)}</p>
                          <p className="text-xs text-gray-500">{formatDate(tx.created_at)}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-semibold ${parseFloat(tx.amount) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {parseFloat(tx.amount) >= 0 ? '+' : ''}{formatAmount(tx.amount)} XOF
                        </p>
                        <p className="text-xs text-gray-500">
                          Solde: {formatAmount(tx.balance_after)} XOF
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
                
                {transactions.length >= 10 && (
                  <div className="px-6 py-4 text-center">
                    <button
                      onClick={loadMoreTransactions}
                      className="text-indigo-600 hover:text-indigo-800 font-medium"
                    >
                      Charger plus de transactions...
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Historique des demandes */}
          <div className="md:col-span-2 bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-800">📋 Historique des demandes de dépôt</h2>
            </div>
            
            {depositRequests.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <p>Aucune demande de dépôt</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {depositRequests.map(request => (
                  <div key={request.id} className="px-6 py-4 hover:bg-gray-50 transition">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="font-medium text-gray-800">{formatAmount(request.amount)} XOF</p>
                        <p className="text-sm text-gray-500">
                          {paymentMethods.find(m => m.value === request.payment_method)?.label || request.payment_method}
                        </p>
                        <p className="text-xs text-gray-400">{formatDate(request.created_at)}</p>
                      </div>
                      <div className="text-right">
                        {getStatusBadge(request.status)}
                        {request.admin_notes && request.status === 'rejected' && (
                          <p className="text-xs text-red-600 mt-1">{request.admin_notes}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Modal Dépôt */}
        {showDepositModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-100">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-800">Ajouter des fonds</h2>
                  <button
                    onClick={() => setShowDepositModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <form onSubmit={handleDepositSubmit} className="p-6 space-y-6">
                {/* Montant */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Montant (XOF) *
                  </label>
                  <input
                    type="number"
                    min="1000"
                    step="500"
                    value={depositForm.amount}
                    onChange={(e) => setDepositForm({ ...depositForm, amount: e.target.value })}
                    placeholder="Ex: 10000"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Minimum: 1 000 XOF</p>
                </div>

                {/* Méthode de paiement */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Méthode de paiement *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {paymentMethods.map(method => (
                      <button
                        key={method.value}
                        type="button"
                        onClick={() => setDepositForm({ ...depositForm, payment_method: method.value })}
                        className={`p-3 border rounded-xl text-left transition ${
                          depositForm.payment_method === method.value
                            ? 'border-indigo-500 bg-indigo-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <span className="text-xl">{method.icon}</span>
                        <p className="text-sm font-medium mt-1">{method.label}</p>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Détails du paiement */}
                <div className="bg-gray-50 rounded-xl p-4 space-y-4">
                  <h3 className="font-medium text-gray-800">Détails du paiement</h3>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      Numéro de téléphone utilisé
                    </label>
                    <input
                      type="tel"
                      value={depositForm.payment_details.phone}
                      onChange={(e) => setDepositForm({
                        ...depositForm,
                        payment_details: { ...depositForm.payment_details, phone: e.target.value }
                      })}
                      placeholder="Ex: +225 07XX XXX XXX"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      ID de transaction (si disponible)
                    </label>
                    <input
                      type="text"
                      value={depositForm.payment_details.transaction_id}
                      onChange={(e) => setDepositForm({
                        ...depositForm,
                        payment_details: { ...depositForm.payment_details, transaction_id: e.target.value }
                      })}
                      placeholder="Ex: TXN123456789"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    />
                  </div>
                </div>

                {/* Instructions */}
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <h4 className="font-medium text-amber-800 mb-2">📌 Instructions de paiement</h4>
                  {paymentInstructions ? (
                    <div className="text-sm text-amber-700 whitespace-pre-line">
                      {paymentInstructions}
                    </div>
                  ) : (
                    <ol className="text-sm text-amber-700 space-y-1 list-decimal list-inside">
                      <li>Effectuez le paiement via Mobile Money</li>
                      <li>Notez l'ID de transaction</li>
                      <li>Remplissez ce formulaire</li>
                      <li>Notre équipe validera sous 24h</li>
                    </ol>
                  )}
                  {paymentQrImage && (
                    <div className="mt-3 pt-3 border-t border-amber-200">
                      <p className="text-amber-800 text-sm font-medium mb-2">📱 QR Code de paiement :</p>
                      <img
                        src={getPaymentQrImageUrl(paymentQrImage)}
                        alt="QR code de paiement"
                        className="max-w-[200px] max-h-[200px] rounded-lg border border-amber-300 object-contain"
                      />
                    </div>
                  )}
                </div>

                {/* Boutons */}
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDepositModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-50 transition"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-3 bg-indigo-600 text-white font-semibold rounded-xl hover:bg-indigo-700 transition disabled:opacity-50"
                  >
                    {submitting ? 'Envoi...' : 'Soumettre la demande'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlatformWallet;
